import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ChevronRight, Check } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Testimonials from "@/components/landing/Testimonials";
import FinalCTA from "@/components/landing/FinalCTA";
import SEO, { SITE_URL } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/i18n/I18nContext";
import { GENERATORS } from "@/data/generators";
import { buildBreadcrumbSchema, buildFAQSchema, buildSoftwareApplicationSchema } from "@/lib/seoSchemas";
import { GENERATOR } from "@/constants/testIds";

/**
 * Reusable SEO landing page template for the "/fr/..." and "/en/..." AI music generator hub.
 * Unlike the rest of the site (which follows the global language toggle), the CONTENT here is
 * deliberately locked to the `locale` prop derived from the URL itself - a French-URL page must
 * always show French content to search engines and visitors, regardless of the user's stored
 * site-wide language preference. The shared Navbar/Footer chrome still follows the global
 * toggle (unchanged existing behavior) to avoid any regression on the rest of the app.
 */
export default function GeneratorLanding({ slug, locale }) {
  const { t } = useI18n();
  const entry = GENERATORS[slug];
  const content = entry[locale] || entry.fr;
  const isEn = locale === "en";
  const siteUrl = SITE_URL;

  const hreflang = [{ lang: "fr", path: entry.pathFr }];
  if (entry.pathEn) hreflang.push({ lang: "en", path: entry.pathEn });
  hreflang.push({ lang: "x-default", path: entry.pathFr });

  const breadcrumbItems = isEn
    ? [{ name: "Home", path: "/" }, { name: "AI Music Generators", path: "/en" }, { name: content.h1, path: entry.pathEn }]
    : [{ name: "Accueil", path: "/" }, { name: "Générateurs IA", path: "/fr" }, { name: content.h1, path: entry.pathFr }];

  const jsonLd = [
    buildSoftwareApplicationSchema({ name: content.h1, description: content.metaDescription, url: `${siteUrl}${isEn ? entry.pathEn : entry.pathFr}` }),
    buildFAQSchema(content.faq),
    buildBreadcrumbSchema(breadcrumbItems, siteUrl),
  ];

  const related = (entry.relatedSlugs || [])
    .map((relSlug) => ({ slug: relSlug, entry: GENERATORS[relSlug] }))
    .filter((r) => r.entry && (isEn ? r.entry.pathEn : r.entry.pathFr));

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={content.metaTitle}
        description={content.metaDescription}
        path={isEn ? entry.pathEn : entry.pathFr}
        jsonLd={jsonLd}
        hreflang={hreflang}
      />
      <Navbar />
      <main>
        <nav aria-label="Breadcrumb" data-testid={GENERATOR.breadcrumb} className="mx-auto max-w-4xl px-4 pt-8 sm:px-6">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {breadcrumbItems.map((item, i) => (
              <li key={item.path} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                {i === breadcrumbItems.length - 1 ? (
                  <span className="text-foreground/80">{item.name}</span>
                ) : (
                  <Link to={item.path} className="hover:text-foreground">{item.name}</Link>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <section className="px-4 py-16 sm:px-6 lg:px-8" data-testid={GENERATOR.hero(slug)}>
          <div className="mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {content.badge}
            </div>
            <h1 className="mt-6 font-heading text-4xl font-medium tracking-tight sm:text-5xl lg:text-6xl">{content.h1}</h1>
            <div className="mx-auto mt-6 max-w-2xl space-y-4 text-left text-base leading-relaxed text-muted-foreground sm:text-lg sm:text-center">
              {content.intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <Link to="/create" className="mt-8 inline-block">
              <Button size="lg" className="rounded-full px-8" data-testid={GENERATOR.cta(slug)}>
                {t("landing.heroCta")}
              </Button>
            </Link>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-card p-8 sm:p-10">
            <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">{content.whyAI.title}</h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {content.whyAI.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">{content.howItWorks.title}</h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {content.howItWorks.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center font-heading text-2xl font-medium tracking-tight sm:text-3xl">
              {isEn ? "Step by step" : "Étape par étape"}
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {content.steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {i + 1}
                  </div>
                  <h3 className="mt-4 font-heading text-base font-medium">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl rounded-3xl border border-primary/30 bg-primary/5 p-8 sm:p-10">
            <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">{content.deepDive.title}</h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {content.deepDive.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-center font-heading text-2xl font-medium tracking-tight sm:text-3xl">FAQ</h2>
            <Accordion type="single" collapsible className="mt-8">
              {content.faq.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={GENERATOR.faqItem(i)}>
                  <AccordionTrigger className="text-left font-heading text-base">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{content.conclusion}</p>
            <Link to="/create" className="mt-6 inline-block">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                {isEn ? "Try Kalypso Music for free" : "Essayez Kalypso Music gratuitement"}
                <Check className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {related.length > 0 && (
          <section className="px-4 py-14 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <h2 className="font-heading text-xl font-medium tracking-tight sm:text-2xl">
                {isEn ? "Explore other AI music styles" : "Découvrez d'autres styles de musique IA"}
              </h2>
              <div className="mt-6 flex flex-wrap gap-3">
                {related.map((r) => {
                  const relContent = r.entry[locale] || r.entry.fr;
                  const relPath = isEn ? r.entry.pathEn : r.entry.pathFr;
                  return (
                    <Link
                      key={r.slug}
                      to={relPath}
                      data-testid={GENERATOR.relatedLink(r.slug)}
                      className="rounded-full border border-border bg-card px-5 py-2.5 text-sm text-foreground/90 transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      {relContent.h1}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <Testimonials />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
